/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import saveAs from 'file-saver';
import { useLocalStorage } from 'react-use';
import { makeStyles } from '@material-ui/core/styles';
import {
  CustomPaging,
  DataTypeProvider,
  IntegratedSorting,
  PagingState,
  RowDetailState,
  SearchState,
  SortingState
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import {
  ColumnChooser,
  DragDropProvider,
  ExportPanel,
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  TableHeaderRow,
  TableRowDetail,
  Toolbar
} from '@devexpress/dx-react-grid-material-ui';
import { Alert, Button, Chip, IconButton, Paper, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import ResponsiveTableContainer, { StickyTableHead } from '../common/ResponsiveTableContainer';
import invoiceService from '../../services/InvoiceService';

const STATUS_OPTIONS = [
  { value: 'Processing', label: 'Processing' },
  { value: 'Extracted', label: 'Extracted' },
  { value: 'PartiallyExtracted', label: 'Partially extracted' },
  { value: 'NotExtracted', label: 'Not extracted' },
  { value: 'Failed', label: 'Failed' }
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((option) => [option.value, option.label]));

const COLUMNS = [
  { name: 'sender', title: 'Sender' },
  { name: 'subject', title: 'Subject' },
  { name: 'receivedAt', title: 'Received' },
  { name: 'status', title: 'Outcome' },
  { name: 'attachmentSummary', title: 'Attachments' },
  { name: 'invoiceCount', title: 'Invoices' },
  { name: 'webLink', title: 'Original Email' }
];

const DEFAULT_WIDTHS = [
  { columnName: 'sender', width: 230 },
  { columnName: 'subject', width: 310 },
  { columnName: 'receivedAt', width: 175 },
  { columnName: 'status', width: 175 },
  { columnName: 'attachmentSummary', width: 225 },
  { columnName: 'invoiceCount', width: 115 },
  { columnName: 'webLink', width: 145 }
];

const getRowId = (row) => row.id;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : format(date, 'dd/MM/yyyy HH:mm');
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const attachmentSummary = (record) => {
  const selected = (record.attachments || []).filter(({ status }) => status === 'Selected').length;
  const rejected = (record.attachments || []).filter(({ status }) => status === 'Rejected').length;
  return [
    `${record.attachmentCount || 0} total`,
    selected ? `${selected} selected` : '',
    rejected ? `${rejected} rejected` : ''
  ]
    .filter(Boolean)
    .join(' · ');
};

const DateFormatter = ({ value }) => <span>{formatDate(value)}</span>;

const SenderFormatter = ({ row }) => (
  <div>
    <div style={{ fontWeight: 600 }}>{row.senderName || 'Unknown sender'}</div>
    {row.senderAddress && <div style={{ color: '#667085', fontSize: 12 }}>{row.senderAddress}</div>}
  </div>
);

const SubjectFormatter = ({ value, row }) => (
  <div>
    <div style={{ fontWeight: 600 }}>{value || '(No subject)'}</div>
    <div style={{ color: '#667085', fontSize: 12 }}>Attempt {row.attemptCount || 1}</div>
  </div>
);

const StatusFormatter = ({ value }) => {
  const color = value === 'Extracted' ? 'success' : value === 'Failed' ? 'error' : 'warning';
  return <Chip label={STATUS_LABELS[value] || value || '-'} color={color} size="small" variant="outlined" />;
};

const InvoiceCountFormatter = ({ value, row, onShowInvoices }) =>
  value ? (
    <Button size="small" onClick={() => onShowInvoices(row)}>
      {value} {value === 1 ? 'bill' : 'bills'}
    </Button>
  ) : (
    <span>-</span>
  );

const WebLinkFormatter = ({ value }) =>
  value ? (
    <Button
      component="a"
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      endIcon={<OpenInNewIcon fontSize="small" />}
    >
      Open
    </Button>
  ) : (
    <span>Unavailable</span>
  );

const DetailToggleCell = ({ expanded, onToggle, ...props }) => (
  <Table.Cell {...props}>
    <IconButton aria-label={expanded ? 'Hide email details' : 'Show email details'} onClick={onToggle} size="small">
      {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  </Table.Cell>
);

const EmailDetail = ({ row }) => (
  <div style={{ backgroundColor: '#f8fafc', padding: 16 }}>
    <div style={{ color: '#344054', fontWeight: 600 }}>{row.statusMessage}</div>
    {row.errorMessage && (
      <Alert severity="error" sx={{ mt: 1 }}>
        {row.errorMessage}
      </Alert>
    )}
    <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
      {(row.attachments || []).length ? (
        row.attachments.map((attachment, index) => (
          <div
            key={attachment.attachmentId || `${row.id}-${index}`}
            style={{
              alignItems: 'flex-start',
              background: '#fff',
              border: '1px solid #e4e7ec',
              borderRadius: 8,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              padding: '10px 12px'
            }}
          >
            <Chip
              label={attachment.status}
              color={attachment.status === 'Selected' ? 'success' : 'default'}
              size="small"
            />
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ fontWeight: 600 }}>{attachment.name || 'Unnamed attachment'}</div>
              <div style={{ color: '#667085', fontSize: 12 }}>
                {[attachment.contentType, formatBytes(attachment.size)].filter(Boolean).join(' · ')}
              </div>
              <div style={{ color: '#475467', fontSize: 13, marginTop: 3 }}>{attachment.reason}</div>
            </div>
          </div>
        ))
      ) : (
        <span style={{ color: '#667085' }}>This email has no attachments.</span>
      )}
    </div>
  </div>
);

const useToolbarStyles = makeStyles((theme) => ({
  toolbar: {
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1),
    '& > div:first-of-type': {
      display: 'none'
    }
  }
}));

const EmailToolbarRoot = ({ children, total, onRefresh, loading, ...props }) => {
  const classes = useToolbarStyles();
  return (
    <Toolbar.Root {...props} className={classes.toolbar}>
      <strong>{total} emails</strong>
      {children}
      <Tooltip title="Refresh email activity">
        <span>
          <IconButton aria-label="Refresh email activity" onClick={onRefresh} disabled={loading} size="small">
            <RefreshIcon className={loading ? 'tw-animate-spin' : ''} />
          </IconButton>
        </span>
      </Tooltip>
    </Toolbar.Root>
  );
};

const InvoiceEmailLedger = ({ onShowInvoices }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [activeStatus, setActiveStatus] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const [sorting, setSorting] = useState([{ columnName: 'receivedAt', direction: 'desc' }]);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [columnOrder, setColumnOrder] = useLocalStorage(
    'invoiceEmailColumnOrder',
    COLUMNS.map(({ name }) => name)
  );
  const [columnWidths, setColumnWidths] = useLocalStorage('invoiceEmailColumnWidths', DEFAULT_WIDTHS);
  const [hiddenColumns, setHiddenColumns] = useLocalStorage('invoiceEmailHiddenColumns', []);
  const exporterRef = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const timeout = window.setTimeout(() => {
      setPage(1);
      setSubmittedSearch(search.trim());
    }, 350);
    // eslint-disable-next-line no-undef
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let active = true;
    const fetchRecords = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await invoiceService.getInvoiceEmails({
          page,
          limit: pageSize,
          ...(activeStatus && { status: activeStatus }),
          ...(submittedSearch && { search: submittedSearch })
        });
        if (!active) return;
        const data = response.data?.data || {};
        setRecords(data.rows || []);
        setTotal(data.total || 0);
        setStatusCounts(data.statusCounts || {});
      } catch (requestError) {
        if (!active) return;
        setRecords([]);
        setTotal(0);
        setStatusCounts({});
        setError(requestError.response?.data?.message || 'Invoice email activity could not be loaded');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRecords();
    return () => {
      active = false;
    };
  }, [activeStatus, page, pageSize, refreshToken, submittedSearch]);

  const rows = useMemo(
    () =>
      records.map((record) => ({
        ...record,
        id: String(record._id || record.messageId),
        sender: record.senderName || record.senderAddress || 'Unknown sender',
        attachmentSummary: attachmentSummary(record)
      })),
    [records]
  );

  const invoiceCountFormatter = useCallback(
    (props) => <InvoiceCountFormatter {...props} onShowInvoices={onShowInvoices} />,
    [onShowInvoices]
  );
  const toolbarRootComponent = useCallback(
    (props) => (
      <EmailToolbarRoot
        {...props}
        total={total}
        loading={loading}
        onRefresh={() => setRefreshToken((value) => value + 1)}
      />
    ),
    [loading, total]
  );
  const startExport = useCallback((options) => exporterRef.current.exportGrid(options), []);
  const saveExport = (workbook) => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      // eslint-disable-next-line no-undef
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Invoice Emails.xlsx');
    });
  };

  return (
    <div className="tw-space-y-4" data-testid="invoice-email-ledger">
      <Alert severity="info" variant="outlined">
        Every email whose only Outlook category is <strong>Yellow Category</strong> appears here—even when no invoice
        could be extracted. Open the original email to resolve anything marked Not extracted or Failed.
      </Alert>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {STATUS_OPTIONS.map(({ value, label }) => (
          <div
            key={value}
            role="button"
            tabIndex={0}
            aria-pressed={activeStatus === value}
            onClick={() => {
              setPage(1);
              setActiveStatus(activeStatus === value ? '' : value);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              setPage(1);
              setActiveStatus(activeStatus === value ? '' : value);
            }}
            style={{
              flex: '1 1 120px',
              minWidth: 120,
              padding: '12px 20px',
              borderRadius: 8,
              backgroundColor: activeStatus === value ? '#f2740d' : '#3f51b5',
              color: 'white',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.15s ease',
              userSelect: 'none'
            }}
          >
            <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{Number(statusCounts[value]) || 0}</div>
            <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>

      {error && (
        <Alert severity="error" action={<Button onClick={() => setRefreshToken((value) => value + 1)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Paper>
        <Grid rows={rows} columns={COLUMNS} getRowId={getRowId}>
          <RowDetailState expandedRowIds={expandedRowIds} onExpandedRowIdsChange={setExpandedRowIds} />
          <SortingState sorting={sorting} onSortingChange={setSorting} />
          <IntegratedSorting />
          <SearchState value={search} onValueChange={setSearch} />
          <PagingState
            currentPage={page - 1}
            onCurrentPageChange={(value) => setPage(value + 1)}
            pageSize={pageSize}
            onPageSizeChange={(value) => {
              setPage(1);
              setPageSize(value);
            }}
          />
          <CustomPaging totalCount={total} />
          <DataTypeProvider for={['sender']} formatterComponent={SenderFormatter} />
          <DataTypeProvider for={['subject']} formatterComponent={SubjectFormatter} />
          <DataTypeProvider for={['receivedAt']} formatterComponent={DateFormatter} />
          <DataTypeProvider for={['status']} formatterComponent={StatusFormatter} />
          <DataTypeProvider for={['invoiceCount']} formatterComponent={invoiceCountFormatter} />
          <DataTypeProvider for={['webLink']} formatterComponent={WebLinkFormatter} />
          <DragDropProvider />
          <Table
            containerComponent={ResponsiveTableContainer}
            headComponent={StickyTableHead}
            messages={{ noData: loading ? 'Loading invoice emails…' : 'No data' }}
          />
          <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={setColumnWidths} />
          <TableColumnReordering order={columnOrder} onOrderChange={setColumnOrder} />
          <TableHeaderRow showSortingControls />
          <TableRowDetail contentComponent={EmailDetail} toggleCellComponent={DetailToggleCell} />
          <TableColumnVisibility hiddenColumnNames={hiddenColumns} onHiddenColumnNamesChange={setHiddenColumns} />
          <Toolbar rootComponent={toolbarRootComponent} />
          <SearchPanel messages={{ searchPlaceholder: 'Search subject, sender or attachment' }} />
          <ColumnChooser />
          <ExportPanel startExport={startExport} />
          <PagingPanel pageSizes={[25, 50, 100]} />
        </Grid>
        <GridExporter
          ref={exporterRef}
          rows={rows}
          columns={COLUMNS}
          hiddenColumnNames={hiddenColumns}
          getRowId={getRowId}
          onSave={saveExport}
        />
      </Paper>
    </div>
  );
};

EmailToolbarRoot.propTypes = {
  children: PropTypes.any,
  total: PropTypes.number.isRequired,
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

InvoiceEmailLedger.propTypes = {
  onShowInvoices: PropTypes.func.isRequired
};

export default InvoiceEmailLedger;
